// B-110 — shared DOMQL v3.14 wrapper around cytoscape.js.
//
// Uses local cytoscape + cytoscape-dagre packages (both in package.json)
// instead of CDN dynamic imports. CDN dynamic imports caused two issues:
// (1) Parcel did not always pick up CDN URL changes and served stale bundles
// that fell back to the synchronous `cose` layout, which blocks the main
// thread for several seconds with 200+ nodes (B-122/B-124 perceived hang).
// (2) Network-gated CDN calls added variable startup latency.
// The workspace has cytoscape@^3.33 + cytoscape-dagre@^2.5 in its deps so
// the local import path is safe.
//
// Consumer contract:
//   TicketsCanvas: {
//     nodes: [{ id, label, kind, status, parent? }],
//     edges: [{ source, target, kind }],
//     onNodeClick: (nodeData) => void
//   }
//
// Status → color mapping mirrors the ops-dashboard palette
// (/tmp/progress-dashboard.html). These values are library-internal
// (fed to cytoscape's style DSL, not DOMQL) so raw hex is the correct
// idiom here despite CLAUDE.md's "no raw hex" rule — cytoscape can't
// read CSS custom properties from its JS config.

import cytoscape from 'cytoscape'
import cytoscapeDagre from 'cytoscape-dagre'

// Register once — cytoscape-dagre.register() is idempotent.
try { cytoscapeDagre(cytoscape) } catch (_) { /* already registered */ }

const STATUS_COLORS = {
  'shipped': '#4ade80',        // ok
  'in-progress': '#fbbf24',    // warn
  'blocked': '#f87171',        // danger
  'paused': '#8b8b8b',         // muted
  'not-started': '#60a5fa'     // info
}

const KIND_SHAPES = {
  'epic': 'hexagon',
  'story': 'round-rectangle',
  'task': 'ellipse',
  'bug': 'diamond',
  'decision': 'tag'
}

export const TicketsCanvas = {
  tag: 'div',
  width: '100%',
  // 2026-04-24: 85vh explicit height + flexShrink:0 so flex-column
  // parents (CanvasMain.Body uses flow:'y') can't stretch it. Dropped
  // `minHeight: 'L+L+L'` — --spacing-L resolves to ~3072px (11 powers
  // of 1.618 from 1em) so 2×L = ~6144px forced the container to
  // 6730px tall. 85vh alone is authoritative now.
  height: '85vh',
  flexShrink: '0',
  background: 'overlay',
  round: 'A',
  borderWidth: 'V',
  borderStyle: 'solid',
  borderColor: 'line',
  position: 'relative',
  overflow: 'hidden',

  onCreate: async (el, s) => {
    if (typeof window === 'undefined' || !el.node) return
    try {
      // cytoscape + cytoscape-dagre are bundled locally (see imports at top).
      // No CDN round-trip — registration already happened at module load.

      // DOMQL v3.14 stores arbitrary element-level props as-given. Factory
      // callbacks are NOT auto-resolved for custom prop names (only
      // built-in style/layout props are). Call the factory if we got
      // one; pass through arrays + undefined falls back to empty.
      const _resolve = (prop) => {
        if (typeof prop === 'function') {
          try { return prop(el, el.state || { root: {} }) } catch { return [] }
        }
        return prop
      }

      // Cytoscape mounts ONCE in onCreate + doesn't observe later state
      // updates. Consumer pages typically populate nodes/edges in an
      // async onCreate that resolves AFTER this mount fires → we see
      // an empty array at first. Wait up to 5s for the factory to
      // return real data before mounting with whatever we have.
      let rawNodes = _resolve(el.nodes) || []
      let rawEdges = _resolve(el.edges) || []
      if (!rawNodes.length) {
        const start = Date.now()
        while (!rawNodes.length && Date.now() - start < 5000) {
          await new Promise((r) => setTimeout(r, 50))
          rawNodes = _resolve(el.nodes) || []
          rawEdges = _resolve(el.edges) || []
        }
      }

      // If consumer passes node.position, use cytoscape `preset` layout
      // for deterministic grid placement. Otherwise fall back to cose.
      const hasPositions = rawNodes.some((n) => n && n.position &&
        Number.isFinite(n.position.x) && Number.isFinite(n.position.y))

      const elements = [
        ...rawNodes.map((n) => ({
          data: {
            id: n.id,
            label: n.blocked ? `🔒 ${n.label || n.id}` : (n.label || n.id),
            kind: n.kind || 'task',
            status: n.status || 'not-started',
            parent: n.parent || undefined,
            blocked: n.blocked ? 1 : 0,
            _statusColor: STATUS_COLORS[n.status] || STATUS_COLORS['not-started'],
            _shape: KIND_SHAPES[n.kind] || 'ellipse'
          },
          // Cytoscape ignores `position` on compound parents (they
          // auto-size to contain children) but accepts it on leaves +
          // un-parented nodes. Pass through when present.
          ...(hasPositions && n.position ? { position: { x: n.position.x, y: n.position.y } } : {})
        })),
        ...rawEdges.map((e) => ({
          data: {
            id: `${e.source}->${e.target}`,
            source: e.source,
            target: e.target,
            kind: e.kind || 'hierarchy'
          }
        }))
      ]

      const cy = cytoscape({
        container: el.node,
        elements,
        style: [
          // Leaf nodes (tasks + bugs — no children).
          {
            selector: 'node',
            style: {
              'background-color': 'data(_statusColor)',
              'shape': 'data(_shape)',
              'label': 'data(label)',
              'color': '#ededed',
              'font-size': '10px',
              'font-weight': '600',
              'text-valign': 'center',
              'text-halign': 'center',
              'text-wrap': 'wrap',
              'text-max-width': '110px',
              'width': 70,
              'height': 70,
              'border-width': 1,
              'border-color': '#2a2a30'
            }
          },
          // Compound parents (epics + stories) — render as boxes that
          // visually contain their children so the 3-level nesting is
          // legible (B-120 fix).
          {
            selector: ':parent',
            style: {
              'background-color': 'data(_statusColor)',
              'background-opacity': 0.12,
              'border-width': 2,
              'border-color': 'data(_statusColor)',
              'border-opacity': 0.6,
              'label': 'data(label)',
              'color': '#ededed',
              'font-size': '13px',
              'font-weight': '700',
              'text-valign': 'top',
              'text-halign': 'center',
              'text-margin-y': -8,
              'padding': 20,
              'shape': 'round-rectangle',
              'text-wrap': 'wrap',
              'text-max-width': '240px'
            }
          },
          // Epics — larger node + thicker border so they read as roots
          // in the hierarchy tree.
          {
            selector: 'node[kind="epic"]',
            style: {
              'width': 110,
              'height': 110,
              'font-size': '14px',
              'border-width': 3,
              'text-max-width': '150px'
            }
          },
          // Stories — medium-sized, distinct shape (round-rectangle).
          {
            selector: 'node[kind="story"]',
            style: {
              'width': 90,
              'height': 60,
              'font-size': '11px',
              'text-max-width': '130px'
            }
          },
          // Hierarchy edges — parent-child arrows generated from parent_ticket_id.
          // Rendered as subtle solid lines so they don't compete with dep arrows.
          {
            selector: 'edge[kind="hierarchy"]',
            style: {
              'line-color': '#6b7280',
              'target-arrow-color': '#6b7280',
              'target-arrow-shape': 'triangle',
              'curve-style': 'straight',
              'width': 1,
              'opacity': 0.35
            }
          },
          {
            selector: 'edge[kind="dependency"]',
            style: {
              'line-color': '#5eead4',
              'target-arrow-color': '#5eead4',
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'line-style': 'dashed',
              'width': 2,
              'opacity': 0.7
            }
          },
          // MIGRATION Phase 2.1 — ticket_dependencies edges (red for blocked_by/
          // waits_on_ship, teal for related).
          {
            selector: 'edge[kind="dep"]',
            style: {
              'line-color': '#f87171',
              'target-arrow-color': '#f87171',
              'target-arrow-shape': 'triangle',
              'curve-style': 'bezier',
              'line-style': 'dashed',
              'width': 2,
              'opacity': 0.8
            }
          },
          {
            selector: 'edge[kind="related"]',
            style: {
              'line-color': '#5eead4',
              'target-arrow-shape': 'none',
              'curve-style': 'bezier',
              'line-style': 'dotted',
              'width': 1,
              'opacity': 0.5
            }
          },
          // 🔒 overlay for nodes whose blocked_by deps aren't shipped.
          {
            selector: 'node[?blocked]',
            style: {
              'border-color': '#f87171',
              'border-width': 3,
              'border-style': 'dashed'
            }
          }
        ],
        // Layout selection:
        //   - `preset` when the consumer precomputed positions
        //   - `dagre` when hierarchy edges are present (Epic → Story → Task/Bug)
        //     Renders a proper top-down DAG tree. dagre is bundled locally
        //     (cytoscape-dagre in package.json) so no CDN latency.
        //   - `cose` as a last-resort force-directed fallback (graphs with
        //     no hierarchy edges and no preset positions)
        layout: hasPositions ? {
          name: 'preset',
          fit: true,
          padding: 40,
          animate: false
        } : rawEdges.some((e) => e && e.kind === 'hierarchy') ? {
          // Dagre top-down tree: epic roots at top, stories below,
          // tasks + bugs at the leaves. Independent epic subtrees
          // stack side-by-side automatically because dagre builds
          // each connected component into its own column.
          name: 'dagre',
          rankDir: 'TB',
          nodeSep: 18,
          rankSep: 60,
          edgeSep: 10,
          ranker: 'tight-tree',
          padding: 40,
          animate: false,
          fit: true
        } : {
          name: 'cose',
          animate: false,
          randomize: false,
          componentSpacing: 40,
          nodeOverlap: 8,
          idealEdgeLength: 60,
          edgeElasticity: 100,
          numIter: 600,
          initialTemp: 150,
          coolingFactor: 0.95,
          nestingFactor: 1.2,
          gravity: 80
        },
        wheelSensitivity: 0.2
      })

      // Fit the graph to the container after the cose layout stops.
      // Without this, cose can leave the viewport panned off-screen —
      // a 12-node / 85vh container otherwise renders as a black panel.
      cy.one('layoutstop', () => {
        try { cy.fit(undefined, 32) } catch {}
      })

      // Click handling: write to root state under `el.selectedKey` (default
      // 'selectedCanvasNode') so the consumer can render a selected-node
      // info panel / deep-link without passing an onNodeClick closure.
      // Also invokes `el.onNodeClick(data)` if the consumer supplied one
      // (legacy callback path — kept for flexibility).
      const selectedKey = el.selectedKey || 'selectedCanvasNode'
      cy.on('tap', 'node', (evt) => {
        const data = evt.target.data()
        const root = el.state?.root
        if (root && typeof root.update === 'function') {
          root.update({ [selectedKey]: data })
        }
        if (typeof el.onNodeClick === 'function') {
          try { el.onNodeClick(data) } catch {}
        }
      })

      el._cytoscape = cy
    } catch (err) {
      if (el.node) {
        el.node.innerHTML = `<div style="padding:24px;color:#8b8b8b;text-align:center;">` +
          `<strong style="color:#f87171">TicketsCanvas load failed</strong><br>` +
          `<small>${(err && err.message) || 'unknown error'}</small></div>`
      }
      console.error('[TicketsCanvas] load failed', err?.message || err)
    }
  },

  onRemove: (el) => {
    if (el._cytoscape) {
      try { el._cytoscape.destroy() } catch {}
      el._cytoscape = null
    }
  }
}
